import * as z from "zod";
import {
  collectFlowBindingExpressions,
  FLOW_BINDING_NAME_PATTERN,
  inspectFlowExpression,
  type FlowExpressionReference,
} from "./expression.js";

/**
 * Any JSON value accepted by an authoring field (`input.example`,
 * `defaults.run_input`, node bindings, JSON Schema documents).
 *
 * Structurally identical to zod's own `util.JSONType`, and mutually assignable
 * with it — the only difference is that the array member is a **named**
 * interface instead of the anonymous `JSONType[]`.
 *
 * That difference is load-bearing for every frontend consumer. Vue's
 * `UnwrapRef<T>` maps an array member to a fresh anonymous mapped type at each
 * level (`{ [K in keyof T]: UnwrapRefSimple<T[K]> }`), so TypeScript's
 * recursion-identity check never fires on a self-recursive JSON alias and the
 * instantiation depth limit is hit instead:
 *
 * ```
 * error TS2589: Type instantiation is excessively deep and possibly infinite.
 * ```
 *
 * A consumer sees that error on their own `const d = ref<FlowDefinition>(…)`
 * line, with nothing pointing back here. Naming the array member gives the
 * recursion a stable identity, so the check fires and `ref<FlowDefinition>()`
 * resolves. Verified against both TypeScript 5.9 and 6.0.
 *
 * Do not replace `FlowJsonArray` with `FlowJsonValue[]` — that reintroduces the
 * regression. `packages/bridge-vue/test/flow-definition-vue-ref.test-d.ts`
 * guards it.
 */
export interface FlowJsonArray extends Array<FlowJsonValue> {}

/** @see {@link FlowJsonArray} for why the array member is a named interface. */
export type FlowJsonValue =
  | string
  | number
  | boolean
  | null
  | FlowJsonArray
  | { [key: string]: FlowJsonValue };

/**
 * `z.json()` retyped to infer {@link FlowJsonValue} instead of zod's
 * `util.JSONType`. Runtime validation is unchanged — this is a type-level
 * substitution only, and the two types are mutually assignable.
 *
 * A **factory**, not a shared constant: `z.toJSONSchema()` extracts any schema
 * instance reused across call sites into a `$defs` entry addressed by `$ref`.
 * Sharing one instance across the five uses below would therefore rewrite the
 * checked-in `flow.v2.schema.json` artifact (and its contract hash) for a purely
 * type-level change. Handing out a fresh instance per call site keeps the
 * generated schema byte-identical — `contract-json-schema.test.ts` guards it.
 */
const jsonValue = (): z.ZodType<FlowJsonValue, FlowJsonValue> =>
  z.json() as unknown as z.ZodType<FlowJsonValue, FlowJsonValue>;

/**
 * Compile-time guard on the claim that the substitution above is type-level
 * only: `FlowJsonValue` and the type `z.json()` actually infers must stay
 * assignable **both ways**, so no caller holding either one is narrowed. Drift
 * on either side violates an `Expect<...>` constraint and fails `tsc` here,
 * rather than surfacing as a surprise at a consumer's call site. Type-only —
 * nothing is emitted.
 */
type ZodJsonValue = z.output<ReturnType<typeof z.json>>;
type ZodJsonInput = z.input<ReturnType<typeof z.json>>;
type AssignableTo<A, B> = [A] extends [B] ? true : false;
type Expect<T extends true> = T;
type _ZodJsonToFlowJson = Expect<AssignableTo<ZodJsonValue, FlowJsonValue>>;
type _FlowJsonToZodJson = Expect<AssignableTo<FlowJsonValue, ZodJsonValue>>;
// The cast asserts both of ZodType's type params, so the input side needs the
// same guard — otherwise drift there passes unnoticed.
type _ZodJsonInputToFlowJson = Expect<AssignableTo<ZodJsonInput, FlowJsonValue>>;
type _FlowJsonToZodJsonInput = Expect<AssignableTo<FlowJsonValue, ZodJsonInput>>;

const nonEmptyString = z.string().min(1);
const flowAssetIdentity = z
  .string()
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Flow identity must be lowercase kebab-case without path separators",
  );
const jsonSchema = z.record(z.string(), jsonValue());
const bindingName = z.string().regex(FLOW_BINDING_NAME_PATTERN, "Binding name is reserved");
const nodeBindings = z.preprocess(
  (value, context) => {
    if (typeof value === "object" && value !== null) {
      for (const key of Object.keys(value)) {
        if (!FLOW_BINDING_NAME_PATTERN.test(key)) {
          context.addIssue({ code: "custom", path: [key], message: "Binding name is reserved" });
        }
      }
    }
    return value;
  },
  z.record(bindingName, jsonValue()),
);

const MISSING_FIELD = Symbol("missing-field");

function declaredFieldSchema(schema: unknown, path: string[]): unknown | typeof MISSING_FIELD {
  let current = schema;
  for (const segment of path) {
    if (typeof current !== "object" || current === null) return MISSING_FIELD;
    const properties = (current as Record<string, unknown>).properties;
    if (
      typeof properties !== "object" ||
      properties === null ||
      !Object.hasOwn(properties, segment)
    ) {
      return MISSING_FIELD;
    }
    current = (properties as Record<string, unknown>)[segment];
  }
  return current;
}

// `labelField` names the input property a host shows in place of a run's opaque
// instance id. It sits beside the schema it points into so every run of the flow
// labels the same way, and a name absent from that schema is an authoring error.
const FlowInputContractSchema = z
  .strictObject({
    schema: jsonSchema,
    example: jsonValue().optional(),
    labelField: nonEmptyString.optional(),
  })
  .superRefine((input, context) => {
    if (input.labelField === undefined) return;
    if (declaredFieldSchema(input.schema, [input.labelField]) === MISSING_FIELD) {
      // Looked up as one literal key, so a dotted name fails here too: a label
      // names a top-level property of the input schema, never a path into it.
      const hint = input.labelField.includes(".")
        ? " (labelField names a single top-level property, not a path)"
        : "";
      context.addIssue({
        code: "custom",
        path: ["labelField"],
        message: `input schema does not declare property ${input.labelField}${hint}`,
      });
    }
  });

const FlowOutputContractSchema = z.strictObject({
  schema: jsonSchema,
});

const PreconditionSchema = z.strictObject({
  expr: nonEmptyString,
  message: nonEmptyString.optional(),
});

const ArtifactContractSchema = z.strictObject({
  kind: nonEmptyString,
  lifetime: z.enum(["session", "persistent"]),
});

const NodeOutputContractSchema = z.strictObject({
  fields: jsonSchema.optional(),
  artifacts: z.array(ArtifactContractSchema).optional(),
});

const NodeContractSchema = z.strictObject({
  requires: z.array(PreconditionSchema).optional(),
  input: nodeBindings.optional(),
  output: NodeOutputContractSchema.optional(),
});

const NodeGateSchema = z.strictObject({
  approval: z.enum(["none", "optional", "mandatory"]),
});

const NodeControlSchema = z.strictObject({
  optional: z.boolean().optional(),
  retries: z.number().int().min(0).optional(),
  timeoutSec: z.number().positive().optional(),
  parallelGroup: nonEmptyString.optional(),
});

const runAssets = { assets: z.array(nonEmptyString).optional() };

// Closed vocabulary, not a free-form model id: the runtime resolves each tier to a
// concrete model, keeping authored flows portable across model-lineup changes.
const modelTier = z.enum(["small", "default", "deep"]);

const AgentRunSchema = z.strictObject({
  kind: z.literal("agent"),
  instruction: nonEmptyString,
  model: modelTier.optional(),
  ...runAssets,
});

const SubpromptRunSchema = z.strictObject({
  kind: z.literal("subprompt"),
  instruction: nonEmptyString,
  model: modelTier.optional(),
  ...runAssets,
});

const deterministicRun = {
  runtime: z.enum(["shell", "node", "python"]).optional(),
  command: nonEmptyString,
  cwd: nonEmptyString.optional(),
  successExit: z.array(z.number().int()).optional(),
  capture: z
    .strictObject({ stdout: nonEmptyString.optional(), stderr: nonEmptyString.optional() })
    .optional(),
  ...runAssets,
};

const FunctionRunSchema = z.strictObject({
  kind: z.literal("function"),
  ...deterministicRun,
});

const CheckRunSchema = z.strictObject({
  kind: z.literal("check"),
  onFail: z.enum(["escalate", "fail"]).default("escalate"),
  ...deterministicRun,
});

const InputChoiceSchema = z.strictObject({
  id: nonEmptyString,
  label: nonEmptyString,
  description: nonEmptyString.optional(),
});

const FormFieldSchema = z.strictObject({
  id: nonEmptyString,
  label: nonEmptyString,
  type: z.enum(["text", "textarea", "number", "choice", "file", "date", "boolean"]),
  required: z.boolean().optional(),
  options: z.array(InputChoiceSchema).optional(),
  accept: z.array(nonEmptyString).optional(),
});

// Exported so the `request_input` write path validates against the same object a
// flow definition does — a second, hand-rolled rule is how the two drifted.
export const InputSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("text"), multiline: z.boolean().optional() }),
  z.strictObject({
    kind: z.literal("choice"),
    options: z.array(InputChoiceSchema).min(1),
    multiple: z.boolean().optional(),
  }),
  z.strictObject({ kind: z.literal("form"), fields: z.array(FormFieldSchema) }),
  z.strictObject({
    kind: z.literal("file"),
    accept: z.array(nonEmptyString).optional(),
    maxSize: z.number().positive().optional(),
    multiple: z.boolean().optional(),
  }),
]);

const GateRunSchema = z.strictObject({
  kind: z.literal("gate"),
  prompt: nonEmptyString,
  schema: InputSchema,
  ...runAssets,
});

const RouteSchema = z.strictObject({
  when: nonEmptyString,
  target: nonEmptyString.nullable(),
});

const RouterRunSchema = z.strictObject({
  kind: z.literal("router"),
  routes: z.array(RouteSchema).min(1),
  ...runAssets,
});

const SubFlowRunSchema = z.strictObject({
  kind: z.literal("sub-flow"),
  flow: flowAssetIdentity,
  passContext: z.boolean().optional(),
  ...runAssets,
});

const RunSchema = z.discriminatedUnion("kind", [
  AgentRunSchema,
  SubpromptRunSchema,
  FunctionRunSchema,
  CheckRunSchema,
  GateRunSchema,
  RouterRunSchema,
  SubFlowRunSchema,
]);

const NodeSchema = z.strictObject({
  id: nonEmptyString,
  label: nonEmptyString,
  description: nonEmptyString,
  phase: nonEmptyString.optional(),
  run: RunSchema,
  contract: NodeContractSchema.optional(),
  gate: NodeGateSchema.optional(),
  control: NodeControlSchema.optional(),
});

const EdgeSchema = z.strictObject({
  id: nonEmptyString,
  source: nonEmptyString,
  target: nonEmptyString,
  type: z.enum(["flow", "parallel", "optional"]),
});

const FlowDefaultsSchema = z.strictObject({
  approval: z.enum(["checkpoint", "auto", "manual"]).optional(),
  researchDepth: nonEmptyString.optional(),
  autoReview: z.boolean().optional(),
  verbosity: nonEmptyString.optional(),
  cliMode: z.boolean().optional(),
  run_input: jsonValue().optional(),
  parameters: z.record(z.string(), jsonValue()).optional(),
});

const FlowMetaSchema = z.strictObject({
  icon: nonEmptyString.optional(),
  category: nonEmptyString.optional(),
  tags: z.array(nonEmptyString).optional(),
  stage: nonEmptyString.optional(),
  onboarding: z
    .strictObject({
      input_style: z.enum(["freeform", "structured", "repo"]),
      placeholder: nonEmptyString.optional(),
      fields: z.array(nonEmptyString).optional(),
    })
    .optional(),
});

const FlowDefinitionObjectSchema = z.strictObject({
  $schema: nonEmptyString.optional(),
  schemaVersion: z.literal(2),
  id: nonEmptyString,
  version: nonEmptyString,
  name: nonEmptyString,
  description: nonEmptyString.optional(),
  input: FlowInputContractSchema.optional(),
  output: FlowOutputContractSchema.optional(),
  defaults: FlowDefaultsSchema.optional(),
  entry: nonEmptyString.optional(),
  nodes: z.array(NodeSchema),
  edges: z.array(EdgeSchema),
  meta: FlowMetaSchema.optional(),
});

/** Validates normalized field shapes without applying authored-v2 graph semantics. */
export function parseFlowDefinitionStructure(data: unknown) {
  return FlowDefinitionObjectSchema.safeParse(data);
}

function knownNonBooleanType(schema: unknown): string | undefined {
  if (typeof schema !== "object" || schema === null) return undefined;
  const type = (schema as Record<string, unknown>).type;
  if (typeof type === "string") return type === "boolean" ? undefined : type;
  if (
    Array.isArray(type) &&
    type.length > 0 &&
    type.every((candidate) => typeof candidate === "string") &&
    type.some((candidate) => candidate !== "boolean")
  ) {
    return type.join(" | ");
  }
  return undefined;
}

/**
 * Why `control.optional` is refused on this node, or `undefined` when it is
 * allowed. `optional` downgrades a failed node to `skipped` so the run carries
 * on — incoherent for a node whose only job is to stop one. Derived in one
 * place so the refusal condition and the message explaining it cannot drift.
 *
 * A `router` is allowed through here but does not downgrade at runtime (see
 * `resolveTerminalFailureStatus`): skipping a router admits every branch at
 * once. Refusing it outright would reject documents the v1 normalizer already
 * emits, so it is accepted and simply never degrades.
 */
function optionalRefusalReason(node: z.infer<typeof NodeSchema>): string | undefined {
  if (node.run.kind === "gate") return "a first-class gate node cannot be optional";
  if (node.run.kind === "check") {
    return "a check node cannot be optional — an optional verification either hides a false green (escalate) or hangs the run forever (fail); reject the check entirely if it should not gate the run";
  }
  if (node.gate?.approval === "mandatory") {
    return "a node cannot be both optional and a mandatory approval gate";
  }
  return undefined;
}

/** Authoritative strict runtime schema for authored, acyclic Flow v2 definitions. */
export const FlowDefinitionSchema = FlowDefinitionObjectSchema.superRefine((flow, context) => {
  const nodeIds = new Set<string>();
  for (const [index, node] of flow.nodes.entries()) {
    if (nodeIds.has(node.id)) {
      context.addIssue({
        code: "custom",
        path: ["nodes", index, "id"],
        message: `duplicate node id: ${node.id}`,
      });
    }
    nodeIds.add(node.id);
    if (node.control?.optional === true) {
      const refusal = optionalRefusalReason(node);
      if (refusal !== undefined) {
        context.addIssue({
          code: "custom",
          path: ["nodes", index, "control", "optional"],
          message: refusal,
        });
      }
    }
  }

  if (flow.entry !== undefined && !nodeIds.has(flow.entry)) {
    context.addIssue({ code: "custom", path: ["entry"], message: `unknown node: ${flow.entry}` });
  }

  const outgoing = new Map<string, Array<{ target: string; path: PropertyKey[] }>>();
  for (const [edgeIndex, edge] of flow.edges.entries()) {
    if (!nodeIds.has(edge.source)) {
      context.addIssue({
        code: "custom",
        path: ["edges", edgeIndex, "source"],
        message: `unknown node: ${edge.source}`,
      });
    }
    if (!nodeIds.has(edge.target)) {
      context.addIssue({
        code: "custom",
        path: ["edges", edgeIndex, "target"],
        message: `unknown node: ${edge.target}`,
      });
    }
    if (edge.type === "flow" && nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      const edges = outgoing.get(edge.source) ?? [];
      edges.push({ target: edge.target, path: ["edges", edgeIndex] });
      outgoing.set(edge.source, edges);
    }
  }

  for (const [nodeIndex, node] of flow.nodes.entries()) {
    if (node.run.kind !== "router") continue;
    for (const [routeIndex, route] of node.run.routes.entries()) {
      if (route.target !== null && !nodeIds.has(route.target)) {
        context.addIssue({
          code: "custom",
          path: ["nodes", nodeIndex, "run", "routes", routeIndex, "target"],
          message: `unknown node: ${route.target}`,
        });
      }
    }
  }

  const validateExpression = (
    expression: string,
    path: PropertyKey[],
    requireBoolean = true,
  ): void => {
    let references: FlowExpressionReference[];
    try {
      references = inspectFlowExpression(expression, requireBoolean);
    } catch (error) {
      context.addIssue({
        code: "custom",
        path,
        message: error instanceof Error ? error.message : "invalid Flow expression",
      });
      return;
    }
    for (const reference of references) {
      const segments = reference.path.split(".");
      if (segments[0] !== "nodes") continue;
      const producer = flow.nodes.find((node) => node.id === segments[1]);
      if (!producer) {
        context.addIssue({
          code: "custom",
          path,
          message: `unknown producer node: ${segments[1]}`,
        });
        continue;
      }
      if (segments[2] === "status") {
        if (reference.booleanOperand) {
          context.addIssue({ code: "custom", path, message: "node status is not boolean" });
        }
        continue;
      }
      const outputSchema = producer.contract?.output?.fields;
      if (!outputSchema) {
        context.addIssue({
          code: "custom",
          path,
          message: `node ${producer.id} has no declared output fields`,
        });
        continue;
      }
      const fieldPath = segments.slice(4);
      const fieldSchema = declaredFieldSchema(outputSchema, fieldPath);
      if (fieldSchema === MISSING_FIELD) {
        context.addIssue({
          code: "custom",
          path,
          message: `node ${producer.id} does not declare output field ${fieldPath.join(".")}`,
        });
        continue;
      }
      const nonBooleanType = reference.booleanOperand
        ? knownNonBooleanType(fieldSchema)
        : undefined;
      if (nonBooleanType) {
        context.addIssue({
          code: "custom",
          path,
          message: `node ${producer.id} output field ${fieldPath.join(".")} is ${nonBooleanType}, not boolean`,
        });
      }
    }
  };

  for (const [nodeIndex, node] of flow.nodes.entries()) {
    for (const [requireIndex, requirement] of node.contract?.requires?.entries() ?? []) {
      validateExpression(requirement.expr, [
        "nodes",
        nodeIndex,
        "contract",
        "requires",
        requireIndex,
        "expr",
      ]);
    }
    for (const [bindingName, binding] of Object.entries(node.contract?.input ?? {})) {
      for (const expression of collectFlowBindingExpressions(binding)) {
        validateExpression(
          expression,
          ["nodes", nodeIndex, "contract", "input", bindingName],
          false,
        );
      }
    }
    if (node.run.kind === "router") {
      for (const [routeIndex, route] of node.run.routes.entries()) {
        validateExpression(route.when, ["nodes", nodeIndex, "run", "routes", routeIndex, "when"]);
      }
    }
  }

  const state = new Map<string, "active" | "done">();
  for (const nodeId of nodeIds) {
    if (state.has(nodeId)) continue;
    const stack: Array<{ nodeId: string; next: number }> = [{ nodeId, next: 0 }];
    state.set(nodeId, "active");
    while (stack.length > 0) {
      const frame = stack.at(-1);
      if (!frame) break;
      const arcs = outgoing.get(frame.nodeId) ?? [];
      if (frame.next >= arcs.length) {
        state.set(frame.nodeId, "done");
        stack.pop();
        continue;
      }
      const arc = arcs[frame.next++];
      if (state.get(arc.target) === "active") {
        context.addIssue({ code: "custom", path: arc.path, message: "flow graph must be acyclic" });
      } else if (!state.has(arc.target)) {
        state.set(arc.target, "active");
        stack.push({ nodeId: arc.target, next: 0 });
      }
    }
  }
});

/** Validated authoring content that is strict, acyclic, and always schema version 2. */
export type FlowDefinition = z.infer<typeof FlowDefinitionSchema>;

/** Graph vertex whose execution identity comes only from its `run.kind`. */
export type FlowNode = FlowDefinition["nodes"][number];

/** Directed dependency link whose authored array position remains content-significant. */
export type FlowEdge = FlowDefinition["edges"][number];

/** Closed execution-owner declaration with no dispatch or conversation-mode switches. */
export type FlowNodeRun = FlowNode["run"];

/** Closed vocabulary of the seven supported execution-owner identities. */
export type FlowNodeKind = FlowNodeRun["kind"];

/** Validation boundary applied to values entering a Flow run. */
export type FlowInputContract = NonNullable<FlowDefinition["input"]>;

/** Validation boundary applied to values leaving a Flow run. */
export type FlowOutputContract = NonNullable<FlowDefinition["output"]>;

/** Node admission and data contract; output typing is paid only when downstream consumers need it. */
export type FlowNodeContract = NonNullable<FlowNode["contract"]>;

/** Availability and failure policy that never changes execution ownership. */
export type FlowNodeControl = NonNullable<FlowNode["control"]>;

/** Named values resolved at the consumer boundary before admission. */
export type FlowNodeBindings = NonNullable<FlowNode["contract"]>["input"];

/** Bounded deterministic admission condition; arbitrary calls and property access are invalid. */
export type FlowNodePrecondition = NonNullable<FlowNode["contract"]>["requires"] extends
  | Array<infer T>
  | undefined
  ? T
  : never;

/** Ordered router branch; the first matching condition owns the routing decision. */
export type FlowRoute = Extract<FlowNodeRun, { kind: "router" }>["routes"][number];

/** Asset requirement independent of node identity and available on every run kind. */
export type FlowAssetRef = NonNullable<FlowNodeRun["assets"]>[number];

/** Model tier for `agent` and `subprompt` runs; `default` inherits the session model
 * (`agent`) or the runtime default (`subprompt`) rather than naming a concrete model. */
export type FlowModelTier = z.infer<typeof modelTier>;
